import {
  FluidExpandedPosition,
  FluidRowData,
  FluidSortHelpers,
  FluidSortOrder,
  FluidTableHeader,
  FluidTableHeaderType,
  getAtPath,
  ID,
} from '@lmig/fluid-core';

import { TABLE_SORT_STATE } from '../../../constants/constants';
import { localStringToNumber } from '../../../utils/formatting/ii8n.utils';
import { FluidBrowserStorage } from '../../../utils/store/local-storage.class';
import { sendEvent } from '../../fluid-data-layer/fluid-data-layer.utils';
import { FluidTable } from '../fluid-table';
import { setGroupedDataSource } from './filter/fluid-table-filter.utils';
import { groupDataSource } from './fluid-table-grouping.utils';
import { getSplitDataPointParent } from './fluid-table-header.utils';
import { applyPagination } from './fluid-table-pagination.utils';

/**
 * Clears any active sort by using the row ID stripped of its identifier
 * to obtain the original index and thus, original sort order, without
 * maintaining a reference in memory.
 * @param host
 * @param serverMode
 */
export const unsortDataSource = (host, serverMode) => {
  const stripIdToIndex = (rowId: string): number =>
    parseInt(rowId.replace(`${host.tableId}_`, ''));
  clearSort(host, true);
  host._dataSource = [
    ...host._dataSource.sort((a: FluidRowData, b: FluidRowData) => {
      return stripIdToIndex(a[ID]) < stripIdToIndex(b[ID]) ? -1 : 1;
    }),
  ];
  host._groupedDataSource = groupDataSource(host.headers, host._dataSource);
  if (!serverMode) {
    applyPagination(host);
  }
  host.sortCleared.emit(true);
};

/**
 * Sort by the given dataPath and update the tables internal state.
 * @param host
 * @param sortedBy
 * @param header
 * @param direction
 * @param serverMode
 */
export const sortBy = (
  host,
  sortedBy: string,
  header: FluidTableHeader,
  direction?: FluidSortOrder,
  serverMode = false,
  isMultiSort = false
): void => {
  if (!isMultiSort) clearSort(host);

  const sortDirection = direction ? direction : header.sortDirection;

  if (header._isSplitHeader) {
    const parentHeader = getSplitDataPointParent(host._headers, header);
    const splitDataPoints = parentHeader.splitDataPoints.map((sh) => ({
      ...sh,
      sorted: sh.dataPath === header.dataPath,
      sortDirection,
    }));
    host.updateHeader(parentHeader, { splitDataPoints });
  } else {
    host.updateHeader(header, { sorted: true, sortDirection });
  }
  if (!serverMode) {
    sortData(
      host._dataSource,
      sortDirection,
      sortedBy,
      header.customSort?.[sortDirection],
      header.type,
      host._multiSortKeys
    );
    setGroupedDataSource(host);
  }
  host.tableStateStore.setSortState(header, sortDirection);

  if (!serverMode) {
    applyPagination(host);
  }

  host.tableSorted.emit({
    sortedBy,
    sortDirection,
  });

  if (host.statefulOptions?.sort?.enabled && sortedBy) {
    FluidBrowserStorage.set(
      TABLE_SORT_STATE(host.tableId),
      {
        sortedBy,
        sortDirection,
      },
      host.statefulOptions?.sort?.strategy
    );
  }

  sendEvent(host, host.parentLayerId, 'tableSorted', {
    sortedBy,
    sortDirection,
  });
};

export const clearSort = (host: FluidTable, clearLocal = false) => {
  host._headers = host._headers.map((h: FluidTableHeader) => ({
    ...h,
    sorted: false,
    sortDirection: undefined,
    splitDataPoints: h.splitDataPoints?.map((sh) => ({
      ...sh,
      sorted: false,
      sortDirection: undefined,
    })),
  }));
  host.tableStateStore.clearSortState();
  if (clearLocal) {
    FluidBrowserStorage.clear(TABLE_SORT_STATE(host.tableId));
  }
  host._multiSortKeys = [];
};

/**
 * On dataItems change, if there is a defined default sort header, apply the sorting and override
 * the direction if necessary.
 * @param host
 */
export const applyDefaultSort = (host: Partial<FluidTable>) => {
  const defaultSortHeader = host._headers.find((h) => h.sorted);

  if (defaultSortHeader) {
    const sortDirection = defaultSortHeader.sortDirection
      ? defaultSortHeader.sortDirection
      : FluidSortOrder.ASC;
    sortBy(host, defaultSortHeader.dataPath, defaultSortHeader, sortDirection);
  }
};

/**
 * Updates the sortKeys array, ensuring the first key is always the primary key.
 * @param sortKey - The key to sort by.
 * @param type - The type of the column.
 * @param order - The sort order (ASC/DESC).
 */

type MultiSortKey = {
  key: string;
  type: FluidTableHeaderType;
  order: FluidSortOrder;
  keyType?: string;
};
const updateSortKeys = (
  sortKey: string,
  type: FluidTableHeaderType,
  order: FluidSortOrder,
  multiSortKeys: MultiSortKey[]
) => {
  const existingIndex = multiSortKeys.findIndex(
    (entry) => entry.key === sortKey && entry.type === type
  );

  if (existingIndex === -1) {
    multiSortKeys.push({
      key: sortKey,
      type,
      order,
      keyType:
        multiSortKeys.length === 0
          ? 'primaryKey'
          : `secondaryKey${multiSortKeys.length}`,
    });
  } else {
    multiSortKeys[existingIndex].order = order;
    if (existingIndex === 0) {
      multiSortKeys[existingIndex].keyType = 'primaryKey';
    }
  }
};

export const sortData = (
  data: any[],
  order: FluidSortOrder,
  sortKey: string,
  customSorter?: (a: any, b: any) => number,
  type: FluidTableHeaderType = FluidTableHeaderType.TEXT,
  multiSortKeys: MultiSortKey[] = []
) => {
  updateSortKeys(sortKey, type, order, multiSortKeys);

  if (customSorter) {
    return data.sort(customSorter);
  }

  if (type === FluidTableHeaderType.DATE && multiSortKeys.length === 1) {
    return FluidSortHelpers.sortByDate(data, sortKey, order);
  }

  return data.sort((a, b) => {
    for (const {
      key,
      type = FluidTableHeaderType.TEXT,
      order,
    } of multiSortKeys) {
      const dataA = getCellValueByType(getAtPath(a, key), type);
      const dataB = getCellValueByType(getAtPath(b, key), type);
      const result = sort(order, dataA, dataB);
      if (result !== 0) {
        return result;
      }
    }
    return 0;
  });
};

/**
 * Get the true value of the cell, based on its data type.
 * @param value
 * @param type
 */
export const getCellValueByType = (value: any, type: FluidTableHeaderType) => {
  switch (type) {
    case FluidTableHeaderType.CURRENCY:
      return localStringToNumber(value);
    default:
      return value;
  }
};

/**
 * Convert data to lowercase (if string) before sorting, based on order.
 * @param order
 * @param dataA
 * @param dataB
 */
const sort = (order, dataA, dataB) => {
  const sortable = (value) =>
    typeof value === 'string' ? value?.toLowerCase() : value;
  if (sortable(dataA) === sortable(dataB)) return 0;
  return order === FluidSortOrder.ASC
    ? sortable(dataA) < sortable(dataB)
      ? -1
      : 1
    : sortable(dataA) > sortable(dataB)
    ? -1
    : 1;
};

export const sortRowsBasedOnExpandButtonPosition = (
  expandedButtonPosition: FluidExpandedPosition,
  icon,
  headers,
  defaultBehavior
) => {
  switch (expandedButtonPosition) {
    case FluidExpandedPosition.RIGHT:
      return [...headers, icon];
    case FluidExpandedPosition.LEFT:
      return [icon, ...headers];
    default:
      return defaultBehavior;
  }
};
